import { logger } from '@accedo/logger';
import { promiseFetch } from '@accedo/promise-fetch';
import {
  SideNavigation,
  SideNavigationClasses,
  SideNavigationItem,
  SideNavigationLocation,
} from '@accedo/side-navigation';
import { stylesheet } from 'astroturf';
import { FunctionalComponent, h } from 'preact';
import {
  Dispatch,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'preact/hooks';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { ModalLauncher } from '@accedo/modal';
import { config } from '##/services/store/store';
import { useInterval, useReferredState } from '##/utils/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import { nav } from '##/init';
import KeyEventHelper from '##/utils/KeyEventHelper';

import { SIDE_MENU_NAV_ID, SIGN_OUT_MODAL_NAV_ID } from '##/utils';
import {
  isLoggedIn,
  refreshOnTokenExpiry,
} from '##/services/authentication/Authentication';
import { SignOutModalRef } from '../SignOutPopup/SignOutPopup';

// TODO: move animation away from width property to aid performance
const styles = stylesheet`
  @import '##/styles/import.scss';

  /* stylelint-disable plugin/no-low-performance-animation-properties */
  .side-navigation {
    width: pxToVw(193);
    background-color: rgba(0,0,0, 0.95);
    border-right: 1px solid rgba(250, 250, 250, 0.35);
    white-space: nowrap;
    box-shadow: -2px 0 40px 12px rgba(0, 0, 0, 0.95);
    overflow-x: hidden;
    z-index: 9;
    transition: width 175ms;
  }

  .side-navigation-live-page {
    @extend .side-navigation;

    background-color: rgb(13 18 21);
  }

  .navigation-link {
    width: pxToVw(193);
    padding: pxToVh(3.6) pxToVw(28);
    font-size: 1.655rem;
    font-family: barlow;
    font-weight: 400;
    line-height: pxToVh(40.4);

    &:global(.current-focus) {
      opacity: 1;
      color: $pure-white;
      background:
        transparent
        linear-gradient(90deg, $ten-play-blue 0%, rgb(14, 163, 253) 100%) 0% 0%
        no-repeat padding-box;
    }
  }

  .navigation-icon {
    fill: $pure-white;
    padding: 0;
    padding-top: pxToVh(5);
    height: pxToVh(34);
    width: pxToVw(28);
    margin-right: pxToVw(10);


    &:global(.current-focus) {
      background:
        transparent
        linear-gradient(90deg, $ten-play-blue 0%, rgb(14, 163, 253) 100%) 0% 0%
        no-repeat padding-box;
    }
  }

  .logo-container {
    width: pxToVw(193);
    padding: 0 0 0 pxToVw(24);
    height: pxToVh(36); 

    img {
      height: 100%;
      max-width: fit-content;
    }
  }

  .side-navigation-closed {
    width: pxToVw(84);
    transition: width 175ms;
    background-color: rgba(0,0,0, 0.5);
    box-shadow: none;

    .navigation-link {
      width: pxToVw(84);
      transition: width 175ms;
    }

    .selectedLink {
      background-color: transparent;
    }

    .logo-container {
      height: pxToVh(36);
      padding: 0 pxToVw(24);
    }

    .navigation-icon {
      margin: 0;
    }
  }

  .side-navigation-closed-live-page {
    @extend .side-navigation-closed;

    background-color: rgb(13 18 21);
  }
`;

// eslint-disable-next-line import/no-mutable-exports
export let setNavVisibility: Dispatch<boolean> | null;

const getSidenavHref = (url: string): string => {
  if (url.indexOf('/show') > -1) {
    return '/shows';
  }
  return url;
};

const SIGN_IN = 'sign-in';
const SIGN_OUT = 'sign-out';

const SIGN_OUT_ROUTE = `/${SIGN_OUT}`;
const SIGN_IN_ROUTE = `/${SIGN_IN}`;

const AUTH_ROUTES = [SIGN_IN_ROUTE, SIGN_OUT_ROUTE];

const SIGN_OUT_ACTION = '#sign-out';

export const SideNav: FunctionalComponent<unknown> = () => {
  const [url, urlRef, setUrl] = useReferredState(
    getSidenavHref(window.location.pathname),
  );
  const currentNavIdRef = useRef<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const isAuthenticated = isLoggedIn();
  setNavVisibility = setIsVisible;

  const {
    collapsedImageUrl = '',
    expandedImageUrl = '',
    primary = [],
    secondary = [],
  } = config.$get().app?.menuConfig || {};

  const primaryNavData = primary.map((items) => {
    const liveTV = config.$get().app.enableLiveContent ? ['live-tv'] : [];
    const primaryDataList = ['home', 'shows', 'news', ...liveTV, 'search'];
    const isIncludedOnPrimary = primaryDataList.includes(items.slug);

    const navIcons = (): h.JSX.Element => (
      <img src={items?.logoUrl} className={styles.navigationIcon} />
    );
    const sideMenuItems = {
      href: items.slug === 'home' ? '/' : `/${items.slug}`,
      icon: navIcons,
      linkTitle: items.label,
    };

    return isIncludedOnPrimary ? sideMenuItems : {};
  });

  const settingsApiItem = secondary.find((item) => item.slug === 'setting');
  const signInOutApiItem = secondary.find(
    (item) => item.slug === (isAuthenticated ? SIGN_OUT : SIGN_IN),
  );

  const settingsSideNavItem = {
    href: '/setting',
    icon: () => (
      <img src={settingsApiItem?.logoUrl} className={styles.navigationIcon} />
    ),
    linkTitle: settingsApiItem?.label,
  };

  const signInOutNavItem = {
    href: isAuthenticated ? SIGN_OUT_ACTION : `/${signInOutApiItem?.slug}`,
    icon: () => (
      <img src={signInOutApiItem?.logoUrl} className={styles.navigationIcon} />
    ),
    linkTitle: signInOutApiItem?.label,
  };

  const navLocation: SideNavigationLocation = 'left';

  const setNavUrl = (href: string): void => {
    if (href !== urlRef.current) {
      if (!AUTH_ROUTES.includes(href)) {
        setUrl(getSidenavHref(href));
      }

      setIsExpanded(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      let { revokeToken } = config.$get().app.authConfig;

      if (!revokeToken) {
        const { api: { apiBaseUrl } = {} } = config.$get();
        revokeToken = `${apiBaseUrl}/api/token/revoke`;
      }

      const { accessToken } = config.$get().user?.jwt ?? {};

      await promiseFetch(revokeToken, {
        method: 'POST',
        responseType: 'json',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });
    } catch (err) {
      logger.error('token revoke failed', err);
    }
  };

  const onLinkClick = (href: string): void => {
    if (AUTH_ROUTES.includes(href)) {
      nav.onKeyEvent('RIGHT');
    }

    if (href !== SIGN_IN_ROUTE) {
      PageNavigation.clearSinglePageTracker();
    }

    if (href === SIGN_OUT_ACTION && isAuthenticated) {
      ModalLauncher.open({
        component: SignOutModalRef,
        props: {
          actions: {
            onSignOut: async () => {
              await signOut();
              ModalLauncher.close();
              PageNavigation.navigateTo('/sign-out');
            },
            onExitModal: () => ModalLauncher.close(),
          },
          defaultFocusId: SIGN_OUT_MODAL_NAV_ID,
          navProps: {
            refocusOnUnmount: false,
          },
        },
      });

      return;
    }

    setNavUrl(href);
  };

  const sideNavItems: SideNavigationItem[] = [
    { spacerVh: 5.8 },
    {
      logoSrcUrlClosed: collapsedImageUrl,
      logoSrcUrlExpanded: expandedImageUrl,
    },
    { spacerVh: 16.7 },
    ...primaryNavData,
    { spacerVh: config.$get().app.enableLiveContent ? 16.11 : 23 },
    settingsSideNavItem,
    signInOutNavItem,
  ];

  const isLiveRoute = window.location.pathname === '/live-tv';

  const sideNavClasses: SideNavigationClasses = {
    navDrawerClass: isLiveRoute
      ? styles.sideNavigationLivePage
      : styles.sideNavigation,
    navDrawerClosedClass: isLiveRoute
      ? styles.sideNavigationClosedLivePage
      : styles.sideNavigationClosed,

    navIconClass: styles.navigationIcon,

    navLinkClass: styles.navigationLink,
    navLinkSelectedClass: styles.selectedLink,

    navLogoContainerClass: styles.logoContainer,
  };

  const preloadLogos = (): void => {
    const logoItem = sideNavItems.find((item) => item.logoSrcUrlClosed);
    if (logoItem && logoItem.logoSrcUrlClosed && logoItem.logoSrcUrlExpanded) {
      const { logoSrcUrlClosed, logoSrcUrlExpanded } = logoItem;
      const closedImage = new Image();
      closedImage.src = logoSrcUrlClosed;
      const openImage = new Image();
      openImage.src = logoSrcUrlExpanded;
    }
  };

  const onKeydown = (keyName: VirtualKeys): boolean => {
    const firstItemIdx = sideNavItems.findIndex(
      (item) => item.href && item.linkTitle,
    );
    const lastItemIdx =
      sideNavItems.length -
      1 -
      sideNavItems
        .slice()
        .reverse()
        .findIndex((item) => item.href && item.linkTitle);
    const firstItemNavId = `nav-item-${firstItemIdx}`;
    const lastItemNavId = `nav-item-${lastItemIdx}`;
    if (
      keyName === KeyCodes.vKey.UP &&
      nav.currentFocusNodeId === firstItemNavId
    ) {
      nav.assignFocus(lastItemNavId);
      return true;
    }
    if (
      keyName === KeyCodes.vKey.DOWN &&
      nav.currentFocusNodeId === lastItemNavId
    ) {
      nav.assignFocus(firstItemNavId);
      return true;
    }
    return false;
  };

  useEffect(() => {
    preloadLogos();
    const unlisten = PageNavigation.addRouteChangeListener(
      ({ location }): void => {
        setNavUrl(location.pathname);
      },
    );
    KeyEventHelper.addKeydownListener(onKeydown, 'side-nav-cycler');
    return () => {
      unlisten();
      KeyEventHelper.removeKeydownListener('side-nav-cycler');
    };
  }, []);

  const setSideNavActiveChild = useCallback((): void => {
    const currentActiveChild = nav.getNode(SIDE_MENU_NAV_ID)?.activeChild;

    if (currentActiveChild !== currentNavIdRef.current) {
      nav.setActiveChild(SIDE_MENU_NAV_ID, currentNavIdRef.current);
    }
  }, []);

  useEffect(() => {
    if (!url) {
      return;
    }

    currentNavIdRef.current = `nav-item-${sideNavItems?.findIndex(
      (navItem) => navItem?.href === urlRef.current,
    )}`;

    setSideNavActiveChild();
  }, [url]);

  const onSidenavItemActive = (active: boolean): void => {
    setSideNavActiveChild();

    setIsExpanded(active);
  };

  useInterval(() => {
    if (!isAuthenticated) {
      return;
    }
    refreshOnTokenExpiry();
  }, 600 * 1000);

  return (
    <SideNavigation
      onActiveChange={onSidenavItemActive}
      onLinkClick={onLinkClick}
      items={sideNavItems}
      classNames={sideNavClasses}
      isExpanded={isExpanded}
      isVisible={isVisible}
      side={navLocation}
      currentAppRoute={url === SIGN_OUT_ROUTE ? window.location.pathname : url}
    />
  );
};
